/*
 * Copyright © 2025 DV Bern AG, Switzerland
 *
 * Das vorliegende Dokument, einschliesslich aller seiner Teile, ist urheberrechtlich
 * geschützt. Jede Verwertung ist ohne Zustimmung der DV Bern AG unzulässig. Dies gilt
 * insbesondere für Vervielfältigungen, die Einspeicherung und Verarbeitung in
 * elektronischer Form. Wird das Dokument einem Kunden im Rahmen der Projektarbeit zur
 * Ansicht übergeben, ist jede weitere Verteilung durch den Kunden an Dritte untersagt.
 */

import type {Signal} from '@angular/core';
import {computed, inject} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {checkPresent} from '@dv/shared/code';
import {signalStoreFeature, withComputed} from '@ngrx/signals';
import {UIRouter} from '@uirouter/core';
import '@uirouter/rx/lib/core.augment';
import {map} from 'rxjs';

// eslint-disable-next-line
export function withUIRouterParams<RouterParams extends Record<string, any>, State extends Record<string, any>>(
    mapParams: (params: RouterParams) => State,
    options: { initialValue: State },
) {
    return signalStoreFeature(
        withComputed(() => {
            const uiRouter = inject(UIRouter);

            const mappedParams$ = checkPresent(uiRouter.globals.params$).pipe(
                map(p => mapParams(p as RouterParams)),
            );
            const mappedParams = toSignal(mappedParams$, {initialValue: options.initialValue});

            return Object.fromEntries(
                Object.keys(mappedParams()).map(key => [
                    key,
                    computed(() => mappedParams()[key as keyof State]),
                ]),
            ) as { [K in keyof State]: Signal<State[K]> };
        }),
    );
}
